import React, { type FC, useEffect, useMemo, useRef } from 'react'

import { createPortal } from 'react-dom'

import { IconCloseLarge } from 'assets/svg/sprite'
import './_modal-wrapper.scss'

type Props = {
  children: React.ReactNode
  open: boolean
  setOpen: (key: boolean) => void
  backgroundClickClose?: boolean
  customClass?: string
  hasCloseButton?: boolean
}

const ModalWrapper: FC<Props> = ({
  children,
  open,
  setOpen,
  backgroundClickClose = true,
  customClass = '',
  hasCloseButton = false,
}) => {
  const overlayRef = useRef<HTMLDivElement | null>(null)

  // Lock body scroll while a modal is open (keeps your existing behavior)
  useEffect(() => {
    const cls = 'modal-wrapper-block-scroll'
    if (open) document.body.classList.add(cls)
    else document.body.classList.remove(cls)
    return () => document.body.classList.remove(cls)
  }, [open])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, setOpen])

  // Avoid SSR errors if document is undefined
  const canPortal = typeof document !== 'undefined' && !!document.body

  const overlayEl = useMemo(() => {
    if (!open) return null
    return (
      <div
        ref={overlayRef}
        className={[
          'modal-wrapper-overlay',
          'modal-wrapper-overlay-open',
          customClass || '',
        ].join(' ')}
        role="dialog"
        aria-modal="true"
      >
        <div
          className="modal-wrapper-background"
          onClick={() => backgroundClickClose && setOpen(false)}
        />
        <div
          className="modal-wrapper-card-wrapper"
          // Stop clicks inside card from bubbling to the backdrop
          onClick={(e) => e.stopPropagation()}
        >
          {hasCloseButton && (
            <button
              type="button"
              className="modal-wrapper-close-button"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <IconCloseLarge width="10" height="10" color="#464853" />
            </button>
          )}
          <div className="modal-wrapper-card">
            {children}
          </div>
        </div>
      </div>
    )
  }, [open, customClass, backgroundClickClose, setOpen, hasCloseButton, children])

  if (!open) return null

  // Render at body level so centering is against the viewport, not a transformed ancestor
  return canPortal ? createPortal(overlayEl, document.body) : overlayEl
}

export default ModalWrapper
